import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  ColSize,
  createBadgeCol,
  createCustomCol,
  createDateHasAllTimeCol,
  createIdCol,
  createImagePreviewCol,
  createIndexCol,
  createNumberCol,
  createStatusBadgeCol,
  createTypoCol,
} from '@/src/helpers/agColumnHelper';
import Badge from '@/components/atom/Badge';
import { getStatusBadgeProps } from '@/src/pages/RecyclingRequests/badge';
import { RecyclingRequestProduct } from '@/src/models/RecyclingRequestDto';
import { UNIT_STRINGS } from '@/src/constants/unit';
import { CreditHistoryDto } from '@/src/models/CreditHistoryDto';
import CommunityPostingDto from '@/src/models/CommunityPostingDto';
import { formatter } from '@/src/utils/formatter';
import dayjs from 'dayjs';
import { OPTIONS_CONST } from '@/src/constants/options';

export const customerMagazineColumn = (): ColDef[] => [
  createIndexCol(),
  createStatusBadgeCol({
    field: 'magazineType',
    type: 'isMarketing',
    size: 'sm',
    statusValueFormatter: params =>
      params?.data?.magazine?.magazineType === 'common',
    headerName: '매거진 유형',
  }),
  createCustomCol({
    field: 'postedAt',
    size: 'md',
    cellRenderer: (params: ValueGetterParams) => {
      return `${dayjs(params?.data?.magazine?.magazinePostStartedAt).format(
        'YYYY-MM-DD',
      )} ~ ${dayjs(params?.data?.magazine?.magazinePostEndedAt).format('YYYY-MM-DD')}`;
    },
    headerName: '매거진 게시 기간',
  }),
  createStatusBadgeCol({
    field: 'isExposed',
    type: 'isExposed',
    size: 'sm',
    statusValueFormatter: params => params?.data?.magazine?.isExposed,
    headerName: '노출 여부',
  }),
  createStatusBadgeCol({
    field: 'awardedCreditYn',
    type: 'isAwarded',
    size: 'sm',
    statusValueFormatter: params => params?.data?.magazine?.awardedCreditYn,
    headerName: '크레딧 지급 여부',
  }),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '조회 일시',
  }),
  createTypoCol({
    field: 'title',
    size: 'lg',
    valueFormatter: params => {
      return params?.data?.magazine?.title || '-';
    },
    headerName: '매거진 제목',
  }),
  createDateHasAllTimeCol({
    field: 'gotCreditAt',
    size: 'md',
    valueFormatter: params => {
      return params?.data?.gotCreditAt || '-';
    },
    headerName: '크레딧 획득 일시',
  }),
  createNumberCol({
    field: 'gotCredit',
    size: 'md',
    valueFormatter: params => {
      return params?.data?.gotCredit || 0;
    },
    headerName: `획득 크레딧 (${UNIT_STRINGS.CREDIT})`,
  }),
];

export const challengeColumn = (): ColDef[] => [
  createIdCol(),
  createCustomCol({
    field: 'terminateStatus',
    valueFormatter: params => {
      return params?.data?.challenge?.terminateStatus;
    },
    cellRenderer: ({ data }: ICellRendererParams) => {
      if (data) {
        return (
          <Badge badgeColor={data ? 'lightYellow' : 'lightGray'}>
            {data ? '진행 중' : '종료'}
          </Badge>
        );
      }
    },
    size: 'sm',
    headerName: '진행 상태',
  }),
  createTypoCol({
    field: 'startedAt',
    valueFormatter: params => {
      const startedAt = params.data?.challenge?.startedAt;
      const endedAt = params.data?.challenge?.endedAt;
      return `${dayjs(startedAt).format('YYYY-MM-DD') || '-'} ~ ${
        dayjs(endedAt).format('YYYY-MM-DD') || '-'
      }`;
    },
    size: 'md',
    headerName: '챌린지 기간',
  }),
  createStatusBadgeCol({
    field: 'isExposed',
    type: 'isExposed',
    size: 'sm',
    statusValueFormatter: params => params?.data?.challenge?.isExposed,
    headerName: '노출 여부',
  }),
  createStatusBadgeCol({
    field: 'isSuccess',
    type: 'isSuccess',
    size: 'sm',
    headerName: '성공 여부',
  }),
  createTypoCol({
    field: 'title',
    valueFormatter: params => {
      return `${params.data?.challenge?.title || '-'}`;
    },
    size: 'lg',
    headerName: '챌린지 제목',
  }),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '참여 일시',
  }),
  createTypoCol({
    field: 'credit',
    size: 'sm',
    valueFormatter: params => {
      return `${Number(params.value).toLocaleString() || 0}${UNIT_STRINGS.CREDIT}`;
    },
    headerName: `획득 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
  createImagePreviewCol({
    field: 'challengeImage',
    width: 40,
    height: 40,
    isPreviewRatioFixed: false,
    valueFormatter: params => {
      return params.data?.challengeImage?.bufferFullUrl || '';
    },
    headerName: '참여 사진',
  }),
];

export const customerDonationColumn = (): ColDef[] => [
  createIdCol(),
  createTypoCol({
    field: 'terminateStatus',
    size: 'md',
    cellRenderer: ({ data }: ICellRendererParams) => {
      const isEnded =
        dayjs(data?.donation.endedAt).isBefore(dayjs(), 'days') ||
        data?.terminateStatus === 'PERIOD_EXPIRED';

      return (
        <Badge badgeColor={isEnded ? 'lightGray' : 'lightYellow'}>
          {isEnded ? '종료' : '진행 중'}
        </Badge>
      );
    },
    headerName: '진행 상태',
  }),
  createCustomCol({
    field: 'startedAt',
    cellRenderer: ({ data }: ICellRendererParams) =>
      `${dayjs(data?.donation.startedAt).format('YYYY-MM-DD')} ~ ${dayjs(
        data?.donation.endedAt,
      ).format('YYYY-MM-DD')}`,
    size: 'md',
    headerName: '모금 기간',
  }),
  createStatusBadgeCol({
    field: 'isExposed',
    type: 'isExposed',
    size: 'sm',
    statusValueFormatter: params => params?.data?.donation.isExposed,
    headerName: '노출 여부',
  }),
  createTypoCol({
    field: 'title',
    cellRenderer: ({ data }: ICellRendererParams) =>
      data?.donation.title || '-',
    size: 'lg',
    headerName: '기부 제목',
  }),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '기부 일시',
  }),
  createCustomCol({
    field: 'donationCredit',
    cellRenderer: ({ data }: ICellRendererParams) =>
      `${(data?.donationCredit || 0).toLocaleString()}${UNIT_STRINGS.CREDIT}`,
    size: 'md',
    headerName: `참여 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
];

export const customerEntryColumn = (): ColDef[] => [
  createIdCol(),
  createTypoCol({
    field: 'terminateStatus',
    size: 'md',
    cellRenderer: ({ data }: ICellRendererParams) => {
      const isEnded =
        dayjs(data?.entry.endedAt).isBefore(dayjs(), 'days') ||
        data?.entry.terminateStatus !== null;

      if (data?.entry.firstServeEndedAt) {
        return (
          <Badge badgeColor={isEnded ? 'lightGray' : 'lightYellow'}>
            선착순 마감
          </Badge>
        );
      }

      return (
        <Badge badgeColor={isEnded ? 'lightGray' : 'lightYellow'}>
          {isEnded ? '종료' : '진행 중'}
        </Badge>
      );
    },
    headerName: '진행 상태',
  }),
  createCustomCol({
    field: 'startedAt',
    size: 'md',
    cellRenderer: ({ data }: ICellRendererParams) =>
      `${dayjs(data?.entry.startedAt).format('YYYY-MM-DD')} ~ ${dayjs(
        data?.entry.endedAt,
      ).format('YYYY-MM-DD')}`,
    headerName: '응모 기간',
  }),
  createStatusBadgeCol({
    field: 'isExposed',
    type: 'isExposed',
    size: 'sm',
    statusValueFormatter: params => params?.data?.entry.isExposed,
    headerName: '노출 여부',
  }),
  createTypoCol({
    field: 'title',
    size: 'lg',
    cellRenderer: ({ data }: ICellRendererParams) => data?.entry.title,
    headerName: '응모 제목',
  }),
  createCustomCol({
    field: 'createdAt',
    size: 'md',
    cellRenderer: ({ data }: ICellRendererParams) => {
      return data?.createdAt
        ? dayjs(data?.createdAt).format('YYYY-MM-DD HH:mm:ss')
        : '-';
    },
    headerName: '응모 일시',
  }),
  createCustomCol({
    field: 'entryCredit',
    cellRenderer: ({ data }: ICellRendererParams) =>
      `${(data?.entryCredit || 0).toLocaleString()}${UNIT_STRINGS.CREDIT}`,
    size: 'md',
    headerName: `참여 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
];

export const customerCommentColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '작성 일시',
  }),
  createBadgeCol({
    field: 'communityReportType',
    options: [
      { label: '신고 접수', value: 'reception', color: 'lightYellow' },
      { label: '블라인드', value: 'blind', color: 'lightRed' },
      { label: '일반', value: 'common', color: 'lightPurple' },
    ],
    size: 'sm',
    headerName: '상태',
  }),
  createTypoCol({
    field: 'content',
    size: 'lg',
    headerName: '내용',
  }),
  createCustomCol({
    field: 'counts',
    size: 'xs',
    cellRenderer: ({ data }: ICellRendererParams) =>
      Number(data?.communityLikeCount || 0).toLocaleString(),
    headerName: '좋아요 수',
  }),
  createDateHasAllTimeCol({
    field: 'blindProcessedAt',
    size: 'md',
    headerName: '처리 일시',
  }),
  createTypoCol({
    field: 'administrator',
    size: 'sm',
    valueFormatter: params => {
      return params?.data?.communityReport?.administrator?.name || '-';
    },
    headerName: '처리 담당자',
  }),
];

export const customerPostingColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '작성 일시',
  }),
  createBadgeCol({
    field: 'communityReportType',
    options: [
      { label: '신고 접수 게시글', value: 'reception', color: 'lightYellow' },
      { label: '블라인드 게시글', value: 'blind', color: 'lightRed' },
      { label: '일반 게시글', value: 'common', color: 'lightPurple' },
    ],
    size: 'sm',
    headerName: '상태',
  }),
  createTypoCol({
    field: 'communityPostingCategoryTypeKr',
    size: 'sm',
    valueFormatter: params => {
      return params?.data?.communityCategory?.categoryName || '-';
    },
    headerName: '카테고리',
  }),
  createTypoCol({ field: 'title', size: 'lg', headerName: '제목' }),
  createCustomCol({
    field: 'counts',
    size: 'xs',
    cellRenderer: ({ data }: ICellRendererParams<CommunityPostingDto>) =>
      `${formatter.NUM_COMMA(data?.communityViewHistoryCount)}`,
    headerName: '조회 수',
  }),
  createDateHasAllTimeCol({
    field: 'blindProcessedAt',
    size: 'md',
    headerName: '처리 일시',
  }),
  createTypoCol({
    field: 'administrator',
    size: 'sm',
    valueFormatter: params => {
      return params?.data?.communityReport?.administrator?.name || '-';
    },
    headerName: '처리 담당자',
  }),
];

export const customerCreditProvideColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '적립 일시',
  }),
  createTypoCol({
    field: 'category',
    size: 'md',
    headerName: '상위 카테고리',
  }),
  {
    field: 'creditTitle',
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ...ColSize.lg,
    cellRenderer: ({ data }: ICellRendererParams<CreditHistoryDto>) => {
      return data?.creditTemplate?.title || data?.subDescription || '직접 입력';
    },
    headerName: '하위 카테고리',
  },
  createTypoCol({
    field: 'value',
    size: 'md',
    valueFormatter: params => {
      return `${Number(params.value).toLocaleString()}`;
    },
    headerName: `적립 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
  createDateHasAllTimeCol({
    field: 'expiredAt',
    size: 'md',
    headerName: '소멸 예정 일시',
  }),
];

export const customerCreditUseColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '사용 일시',
  }),
  createTypoCol({
    field: 'category',
    size: 'md',
    headerName: '상위 카테고리',
  }),
  {
    field: 'creditTitle',
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ...ColSize.lg,
    cellRenderer: ({ data }: ICellRendererParams<CreditHistoryDto>) => {
      return data?.creditTemplate?.title || data?.subDescription || '-';
    },
    headerName: '하위 카테고리',
  },
  createTypoCol({
    field: 'value',
    size: 'md',
    valueFormatter: params => {
      return `${Number(params.value).toLocaleString()}`;
    },
    headerName: `사용 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
];

export const customerCreditExpiredColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '소멸 일시',
  }),
  createTypoCol({
    field: 'category',
    size: 'md',
    headerName: '상위 카테고리',
  }),
  {
    field: 'creditTitle',
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ...ColSize.lg,
    cellRenderer: ({ data }: ICellRendererParams<CreditHistoryDto>) => {
      return data?.creditTemplate?.title || data?.subDescription || '-';
    },
    headerName: '하위 카테고리',
  },
  createTypoCol({
    field: 'value',
    size: 'md',
    valueFormatter: params => {
      return `${Number(params.value).toLocaleString()}`;
    },
    headerName: `소멸 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
];

export const customerCreditDeductionColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '차감 일시',
  }),
  createTypoCol({
    field: 'category',
    size: 'md',
    headerName: '상위 카테고리',
  }),
  {
    field: 'creditTitle',
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ...ColSize.lg,
    cellRenderer: ({ data }: ICellRendererParams<CreditHistoryDto>) => {
      if (!data?.administrator) {
        return `${data?.creditTemplate?.title || data?.subDescription || '직접 입력'} 지급 크레딧 차감`;
      }

      return data?.creditTemplate?.title || data?.subDescription || '직접 입력';
    },
    headerName: '하위 카테고리',
  },

  createTypoCol({
    field: 'value',
    size: 'md',
    valueFormatter: params => {
      return `${Number(params.value).toLocaleString()}`;
    },
    headerName: `차감 크레딧(${UNIT_STRINGS.CREDIT})`,
  }),
  {
    field: 'administrator',
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ...ColSize.sm,
    cellRenderer: ({ data }: ICellRendererParams<CreditHistoryDto>) => {
      return data?.administrator?.name || '시스템';
    },
    headerName: '처리 담당자',
  },
];

export const customerRecyclingRequestColumn = (): ColDef[] => [
  createIdCol(),
  createTypoCol({
    field: 'reqNo',
    size: 'lg',
    headerName: '수거 번호',
  }),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '수거 신청 일시',
  }),
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'status',
    ...ColSize.md,
    cellRenderer: (params: ValueGetterParams) => {
      return <Badge {...getStatusBadgeProps(params?.data?.status)} />;
    },
    headerName: '수거 진행 상태',
  },
  createTypoCol({
    field: 'boxCount',
    size: 'sm',
    headerName: '수거 박스 수',
  }),
  createTypoCol({
    field: 'totalWeight',
    size: 'md',
    valueFormatter: params =>
      `${(params.data as RecyclingRequestProduct)?.totalWeight || 0}${
        UNIT_STRINGS.GRAM
      }`,
    headerName: '총 무게',
  }),
  createTypoCol({
    field: 'totalCarbonEmissionReduction',
    size: 'md',
    valueFormatter: params =>
      `${
        (params.data as RecyclingRequestProduct)
          ?.totalCarbonEmissionReduction || 0
      }${UNIT_STRINGS.GRAM}`,
    headerName: '탄소감축량',
  }),
  createTypoCol({
    field: 'totalPineScore',
    size: 'md',
    headerName: '소나무 지수',
  }),
];

export const customerAddressColumn = (): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '등록 일시',
  }),
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'isBaseAddress',
    ...ColSize.sm,
    cellRenderer: (params: ValueGetterParams) => {
      if (params?.data?.isBaseAddress === true) {
        return (
          <Badge className="h-[20px]" badgeColor="lightPurple">
            {OPTIONS_CONST.assigned}
          </Badge>
        );
      }
      if (params?.data?.isBaseAddress === false) {
        return (
          <Badge className="h-[20px]" badgeColor="lightGray">
            {OPTIONS_CONST.notAssigned}
          </Badge>
        );
      }
      return '-';
    },
    headerName: '기본 주소 여부',
  },
  createTypoCol({
    field: 'street',
    size: 'lg',
    cellRenderer: ({ data }: ICellRendererParams) => {
      return `${data?.street || ''}${data?.detail ? `, ${data?.detail}` : ''}`;
    },
    headerName: '주소',
  }),
];
